import { useCallback } from "react";

/**
 * Hook that provides utility functions for formatting variable values
 * used in dynamic content resolution (dates, images, stringification).
 */
export function useVariableFormatting() {
	const stringifyVariableValue = useCallback((value: any): string => {
		if (Array.isArray(value)) {
			return value
				.map((entry) => stringifyVariableValue(entry))
				.filter(Boolean)
				.join(", ");
		}
		if (value && typeof value === "object") {
			return JSON.stringify(value);
		}
		if (value === null || value === undefined) return "";
		return String(value);
	}, []);

	const parseDynamicDate = useCallback((rawValue: any): Date | null => {
		if (rawValue == null) return null;
		const raw = String(rawValue).trim();
		if (!raw) return null;
		if (/^\d{2}\/\d{2}\/\d{4}$/.test(raw)) {
			const [day, month, year] = raw.split("/");
			const parsed = new Date(Number(year), Number(month) - 1, Number(day));
			return Number.isNaN(parsed.getTime()) ? null : parsed;
		}
		if (/^\d{2}-\d{2}-\d{4}$/.test(raw)) {
			const [day, month, year] = raw.split("-");
			const parsed = new Date(Number(year), Number(month) - 1, Number(day));
			return Number.isNaN(parsed.getTime()) ? null : parsed;
		}
		if (/^\d{4}-\d{2}-\d{2}/.test(raw)) {
			const [year, month, day] = raw.slice(0, 10).split("-");
			const parsed = new Date(Number(year), Number(month) - 1, Number(day));
			return Number.isNaN(parsed.getTime()) ? null : parsed;
		}
		const parsed = new Date(raw);
		return Number.isNaN(parsed.getTime()) ? null : parsed;
	}, []);

	const formatDynamicDate = useCallback(
		(rawValue: any): string => {
			const parsed = parseDynamicDate(rawValue);
			if (!parsed) return stringifyVariableValue(rawValue);
			try {
				return new Intl.DateTimeFormat("fr-FR", {
					day: "2-digit",
					month: "long",
					year: "numeric"
				}).format(parsed);
			} catch {
				return stringifyVariableValue(rawValue);
			}
		},
		[parseDynamicDate, stringifyVariableValue]
	);

	const extractImageVariableValue = useCallback(
		(rawValue: any): string => {
			if (rawValue == null) return "";
			if (typeof rawValue === "string") return rawValue;
			if (typeof rawValue === "object") {
				const src = (rawValue as { src?: string }).src;
				if (typeof src === "string") return src;
			}
			return stringifyVariableValue(rawValue);
		},
		[stringifyVariableValue]
	);

	const formatVariableValue = useCallback(
		(rawValue: any, fieldType?: string | null): string => {
			const normalizedType = (fieldType ?? "").trim().toLowerCase();
			if (normalizedType === "date") {
				return formatDynamicDate(rawValue);
			}
			if (normalizedType === "image") {
				return extractImageVariableValue(rawValue);
			}
			return stringifyVariableValue(rawValue);
		},
		[extractImageVariableValue, formatDynamicDate, stringifyVariableValue]
	);

	return {
		stringifyVariableValue,
		parseDynamicDate,
		formatDynamicDate,
		extractImageVariableValue,
		formatVariableValue
	};
}
